import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";

import InventoryService from "../../../api/inventory";

const ADJUSTMENT_TYPES = [
  { value: "manual_adjustment", label: "Ajuste manual (pode ser + ou -)" },
  { value: "loss", label: "Perda / Desperdicio (valor negativo)" },
];

const StockAdjustmentModal = ({ open, onClose, onSuccess, showAlert }) => {
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    ingredient: null,
    quantity: "",
    movement_type: "manual_adjustment",
    notes: "",
    reference: "",
  });

  useEffect(() => {
    if (open) {
      loadIngredients();
      setForm({
        ingredient: null,
        quantity: "",
        movement_type: "manual_adjustment",
        notes: "",
        reference: "",
      });
    }
  }, [open]);

  const loadIngredients = async () => {
    try {
      const res = await InventoryService.getIngredients({ page_size: 500 });
      setIngredients(res.data.results || []);
    } catch (err) {
      console.error("Error loading ingredients:", err);
    }
  };

  const handleSubmit = async () => {
    if (!form.ingredient || !form.quantity) return;

    let quantity = Number(form.quantity);
    if (form.movement_type === "loss" && quantity > 0) {
      quantity = -quantity;
    }

    setLoading(true);
    try {
      await InventoryService.createStockAdjustment({
        ingredient: form.ingredient.id,
        quantity,
        movement_type: form.movement_type,
        notes: form.notes,
        reference: form.reference,
      });
      onSuccess();
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.detail ||
        "Erro ao registrar ajuste";
      showAlert(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Ajuste de Estoque</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <Autocomplete
              options={ingredients}
              getOptionLabel={(opt) =>
                `${opt.name} (${opt.unit}) — Atual: ${opt.current_quantity}`
              }
              value={form.ingredient}
              onChange={(_, val) => setForm({ ...form, ingredient: val })}
              renderInput={(params) => (
                <TextField {...params} label="Ingrediente" required />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              select
              label="Tipo de Ajuste"
              value={form.movement_type}
              onChange={(e) =>
                setForm({ ...form, movement_type: e.target.value })
              }
              fullWidth
            >
              {ADJUSTMENT_TYPES.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={`Quantidade${form.ingredient ? ` (${form.ingredient.unit})` : ""}`}
              type="number"
              value={form.quantity}
              onChange={(e) => setForm({ ...form, quantity: e.target.value })}
              fullWidth
              required
              inputProps={{ step: 0.001 }}
              helperText={
                form.movement_type === "loss"
                  ? "Sera registrado como valor negativo"
                  : "Positivo = acrescimo, negativo = reducao"
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Motivo / Observacoes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Referencia (opcional)"
              value={form.reference}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
              fullWidth
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!form.ingredient || !form.quantity || loading}
          sx={{
            backgroundColor: "#f59e0b",
            color: "#000",
            "&:hover": { backgroundColor: "#d97706" },
          }}
        >
          {loading ? "Registrando..." : "Registrar Ajuste"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StockAdjustmentModal;
